import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Smartphone, Mail, MessageCircle, Monitor, MousePointerClick, Route, TrendingDown, Flame, ArrowDown, RefreshCw, CheckCircle2, Cog } from 'lucide-react';
interface SlideWhatYouGetProps {
  onContinue: () => void;
}
export function SlideWhatYouGet({
  onContinue
}: SlideWhatYouGetProps) {
  const [layer, setLayer] = useState(-1);
  const [showInsight, setShowInsight] = useState(false);
  useEffect(() => {
    const t0 = setTimeout(() => setLayer(0), 600);
    const t1 = setTimeout(() => setLayer(1), 1600);
    const t2 = setTimeout(() => setLayer(2), 2600);
    const t3 = setTimeout(() => setShowInsight(true), 3400);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-14 md:py-12 lg:px-20 lg:py-14 max-w-7xl mx-auto" data-id="element-399">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-white mb-8 text-center" data-id="element-400">
        What are you <span className="text-teal-400" data-id="element-401">actually</span> getting?
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-id="element-402">
        {/* 3-Layer Stack */}
        <div className="lg:col-span-8 space-y-4" data-id="element-403">
          {/* TOP — Campaigns */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: layer >= 0 ? 1 : 0,
          y: layer >= 0 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-500 rounded-2xl p-5" data-id="element-404">
            <span className="text-xs uppercase tracking-widest text-teal-400 font-semibold mb-4 block" data-id="element-405">
              Campaigns
            </span>
            <div className="grid grid-cols-4 gap-3" data-id="element-406">
              {[{
              icon: <Smartphone className="w-6 h-6" data-id="element-407" />,
              label: 'Snak'
            }, {
              icon: <Mail className="w-6 h-6" data-id="element-408" />,
              label: 'Email'
            }, {
              icon: <MessageCircle className="w-6 h-6" data-id="element-409" />,
              label: 'WhatsApp'
            }, {
              icon: <Monitor className="w-6 h-6" data-id="element-410" />,
              label: 'Congress'
            }].map((ch, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: layer >= 0 ? 1 : 0,
              scale: layer >= 0 ? 1 : 0.8
            }} transition={{
              delay: i * 0.1
            }} className="bg-navy-800/80 border border-navy-600 rounded-xl p-4 flex flex-col items-center gap-2" data-id="element-411">
                  <div className="text-teal-400" data-id="element-412">{ch.icon}</div>
                  <span className="text-xs text-white/70 font-medium" data-id="element-413">
                    {ch.label}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Connection arrow */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: layer >= 1 ? 0.5 : 0
        }} className="flex justify-center" data-id="element-414">
            <ArrowDown className="w-5 h-5 text-teal-400/50" data-id="element-415" />
          </motion.div>

          {/* MIDDLE — Learns & Improves */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: layer >= 1 ? 1 : 0,
          y: layer >= 1 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-5" data-id="element-416">
            <span className="text-xs uppercase tracking-widest text-teal-400/80 font-semibold mb-4 block" data-id="element-417">
              Learns & Improves
            </span>
            <div className="flex items-center justify-between gap-3" data-id="element-418">
              <div className="flex gap-4" data-id="element-419">
                {[{
                icon: <MousePointerClick className="w-5 h-5" data-id="element-420" />,
                label: 'Clicks'
              }, {
                icon: <Route className="w-5 h-5" data-id="element-421" />,
                label: 'Paths'
              }, {
                icon: <TrendingDown className="w-5 h-5" data-id="element-422" />,
                label: 'Drop-offs'
              }, {
                icon: <Flame className="w-5 h-5" data-id="element-423" />,
                label: 'Hotspots'
              }].map((d, i) => <motion.div key={i} initial={{
                opacity: 0
              }} animate={{
                opacity: layer >= 1 ? 1 : 0
              }} transition={{
                delay: 0.2 + i * 0.1
              }} className="flex flex-col items-center gap-1.5" data-id="element-424">
                    <div className="w-10 h-10 rounded-lg bg-navy-800/80 border border-navy-600 flex items-center justify-center text-white/50" data-id="element-425">
                      {d.icon}
                    </div>
                    <span className="text-xs text-white/40" data-id="element-426">{d.label}</span>
                  </motion.div>)}
              </div>

              <motion.div initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: layer >= 1 ? 1 : 0,
              x: layer >= 1 ? 0 : -10
            }} transition={{
              delay: 0.6
            }} className="flex items-center gap-2" data-id="element-427">
                <div className="text-teal-400/50" data-id="element-428">→</div>
                <div className="bg-teal-400/10 border border-teal-400/30 rounded-lg px-4 py-2 flex items-center gap-2" data-id="element-429">
                  <RefreshCw className="w-4 h-4 text-teal-400" data-id="element-430" />
                  <span className="text-xs text-teal-300 font-medium" data-id="element-431">
                    Refined Snak
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Connection arrow */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: layer >= 2 ? 0.5 : 0
        }} className="flex justify-center" data-id="element-432">
            <ArrowDown className="w-5 h-5 text-teal-400/50" data-id="element-433" />
          </motion.div>

          {/* BOTTOM — Snakker Runs It */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: layer >= 2 ? 1 : 0,
          y: layer >= 2 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-teal-400/5 border border-teal-400/20 rounded-2xl p-4 flex items-center justify-center gap-4" data-id="element-434">
            <motion.div animate={layer >= 2 ? {
            rotate: 360
          } : {}} transition={{
            repeat: Infinity,
            duration: 6,
            ease: 'linear'
          }} data-id="element-435">
              <Cog className="w-8 h-8 text-teal-400" data-id="element-436" />
            </motion.div>
            <div data-id="element-437">
              <span className="text-xs uppercase tracking-widest text-teal-400 font-semibold block" data-id="element-438">
                Snakker runs it
              </span>
              <span className="text-xs text-white/40" data-id="element-439">
                Connected to all layers
              </span>
            </div>
          </motion.div>
        </div>

        {/* Insight Box */}
        <div className="lg:col-span-4" data-id="element-440">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: showInsight ? 1 : 0,
          x: showInsight ? 0 : 20
        }} transition={{
          duration: 0.6
        }} className="bg-navy-700/50 border border-navy-600 p-6 rounded-2xl" data-id="element-441">
            <div className="text-teal-400 font-medium mb-4 flex items-center gap-2 text-sm" data-id="element-442">
              <CheckCircle2 className="w-4 h-4" data-id="element-443" />
              Insight
            </div>
            <div className="space-y-4" data-id="element-444">
              <div className="flex items-center gap-3" data-id="element-445">
                <div className="w-8 h-8 rounded-lg bg-navy-600 flex items-center justify-center" data-id="element-446">
                  <span className="text-white/50 text-lg font-bold" data-id="element-447">✕</span>
                </div>
                <span className="text-white/70 font-medium" data-id="element-448">
                  Not a platform
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-449">
                <div className="w-8 h-8 rounded-lg bg-navy-600 flex items-center justify-center" data-id="element-450">
                  <span className="text-white/50 text-lg font-bold" data-id="element-451">✕</span>
                </div>
                <span className="text-white/70 font-medium" data-id="element-452">No setup</span>
              </div>
              <div className="flex items-center gap-3 mt-2" data-id="element-453">
                <div className="w-8 h-8 rounded-lg bg-teal-400/20 flex items-center justify-center" data-id="element-454">
                  <CheckCircle2 className="w-4 h-4 text-teal-400" data-id="element-455" />
                </div>
                <span className="text-teal-300 font-semibold" data-id="element-456">
                  Runs for you
                </span>
              </div>
            </div>

            <div className="mt-6" data-id="element-457">
              <Button onClick={onContinue} data-id="element-458">See how it works</Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}